import moment from 'moment-timezone';
import { DATE_INPUT_FORMAT, TIME_INPUT_FORMAT } from './input-formats';
import {
  AvailableSchedulePeriod,
  ITimeSlotTimestamp,
  LocalAvailableTime,
} from '../models/schedule-calendar.models';

export const MINUTES_PER_DAY = 24 * 60;

/** Part of a period that falls on one day in a timezone */
export interface IDaySegment {
  /** YYYY-MM-DD format in the timezone */
  date: string;
  /** Minutes from the start of the day, where 1440 is the end of the day */
  start: number;
  end: number;
  /** The segment as UTC timestamps */
  range: ITimeSlotTimestamp;
}

/**
 * The start of the day after a YYYY-MM-DD date in the timezone. Parsed rather
 * than added on, as adding a day lands before the start of days the clocks
 * skip midnight on.
 */
const toStartOfNextDay = (date: string, timezoneName: string) =>
  moment.tz(
    moment(date, DATE_INPUT_FORMAT).add(1, 'day').format(DATE_INPUT_FORMAT),
    DATE_INPUT_FORMAT,
    timezoneName
  );

/** When the timezone last changed its offset at or before the time, in ms */
const toLastOffsetChange = (time: moment.Moment, timezoneName: string) =>
  (moment.tz.zone(timezoneName)?.untils ?? [])
    .filter((until) => until <= time.valueOf())
    .at(-1) ?? time.valueOf();

/** The time at a YYYY-MM-DD date and HH:mm time with the UTC offset in minutes */
const atUtcOffset = (date: string, time: string, utcOffset: number) =>
  (time === '24:00'
    ? moment.utc(date, DATE_INPUT_FORMAT).add(1, 'day')
    : moment.utc(`${date} ${time}`, `${DATE_INPUT_FORMAT} ${TIME_INPUT_FORMAT}`)
  ).subtract(utcOffset, 'minutes');

/**
 * A YYYY-MM-DD date and HH:mm time in the timezone as a UTC timestamp. A time
 * of '24:00' is midnight at the end of the day. A time the clocks going back
 * repeats is taken at the preferred UTC offset (in minutes) where it has one,
 * and otherwise at its first occurrence. A time the clocks going forward skip
 * is taken as the moment they go forward.
 */
export const toUTCTimestamp = (
  date: string,
  time: string,
  timezoneName: string,
  preferredUtcOffset?: number
) => {
  if (preferredUtcOffset !== undefined) {
    const preferred = atUtcOffset(date, time, preferredUtcOffset);

    // Only the wall clock time where the timezone has that offset
    if (preferred.clone().tz(timezoneName).utcOffset() === preferredUtcOffset) {
      return preferred.toISOString();
    }
  }

  if (time === '24:00') {
    return toStartOfNextDay(date, timezoneName).toISOString();
  }

  const parsed = moment.tz(
    `${date} ${time}`,
    `${DATE_INPUT_FORMAT} ${TIME_INPUT_FORMAT}`,
    timezoneName
  );

  // Moment moves a time the clocks skip on by the time skipped, which can
  // take a start past an end that does exist
  return parsed.format(TIME_INPUT_FORMAT) === time
    ? parsed.toISOString()
    : new Date(toLastOffsetChange(parsed, timezoneName)).toISOString();
};

export const toAvailableSchedulePeriod = (
  date: string,
  { start, end, isPreferred }: LocalAvailableTime,
  timezoneName: string,
  preferredUtcOffset?: number
): AvailableSchedulePeriod => ({
  start: toUTCTimestamp(date, start, timezoneName, preferredUtcOffset),
  end: toUTCTimestamp(date, end, timezoneName, preferredUtcOffset),
  isPreferred,
});

/**
 * The times on a YYYY-MM-DD date in the timezone as UTC periods, leaving out
 * any wholly within a time the clocks going forward skip
 */
export const toAvailableSchedulePeriods = (
  date: string,
  times: LocalAvailableTime[],
  timezoneName: string,
  preferredUtcOffset?: number
) =>
  times
    .map((time) =>
      toAvailableSchedulePeriod(date, time, timezoneName, preferredUtcOffset)
    )
    .filter(({ start, end }) => start < end);

/** Minutes from the start of the day on the wall clock */
export const toDayMinutes = (time: moment.Moment) =>
  time.hours() * 60 + time.minutes();

/** When the timezone next changes its offset after the time, in ms */
const toNextOffsetChange = (time: moment.Moment, timezoneName: string) =>
  moment.tz
    .zone(timezoneName)
    ?.untils.find((until) => until > time.valueOf()) ?? Infinity;

/**
 * Splits a period into one segment per day it covers in the timezone, and
 * again wherever the clocks change. Minutes follow the wall clock, so times
 * keep their place on days changing the clocks, and each segment keeps one
 * offset so its end never falls before its start when the clocks go back.
 */
export const splitByDay = (
  period: ITimeSlotTimestamp,
  timezoneName: string
): IDaySegment[] => {
  const start = moment(period.start).tz(timezoneName);
  const end = moment(period.end).tz(timezoneName);

  if (!start.isBefore(end)) {
    return [];
  }

  const date = start.format(DATE_INPUT_FORMAT);
  const nextDay = toStartOfNextDay(date, timezoneName);
  // Guards against recursing forever where a zone's date goes back at midnight
  const endsToday = end.isBefore(nextDay) || !nextDay.isAfter(start);
  const segmentEnd = moment(
    Math.min(
      (endsToday ? end : nextDay).valueOf(),
      toNextOffsetChange(start, timezoneName)
    )
  );
  const endsDay = !endsToday && segmentEnd.isSame(nextDay);

  return [
    {
      date,
      start: toDayMinutes(start),
      // Counted on from the start, as the wall clock after an offset change
      // belongs to the next segment
      end: endsDay
        ? MINUTES_PER_DAY
        : toDayMinutes(start) + segmentEnd.diff(start, 'minutes'),
      range: { start: start.toISOString(), end: segmentEnd.toISOString() },
    },
    ...splitByDay(
      { start: segmentEnd.toISOString(), end: period.end },
      timezoneName
    ),
  ];
};
